import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const SuperAdminLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    twoFactorCode: ''
  });
  const [errors, setErrors] = useState({});
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email?.trim()) {
      newErrors.email = 'Admin email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password?.trim()) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (showTwoFactor && !formData?.twoFactorCode?.trim()) {
      newErrors.twoFactorCode = '2FA code is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleLogin = async (e) => {
    e?.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    // Simulate authentication
    setTimeout(() => {
      setIsLoading(false);
      // Navigate to super admin dashboard on success
      navigate('/super-admin-dashboard');
    }, 1500);
  };

  const handleForgotPassword = () => {
    // Handle password recovery workflow
    console.log('Password recovery initiated');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Admin Branding */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-amber-500/20 border-2 border-amber-500/50 mb-4">
            <Icon name="Shield" size={32} color="#F59E0B" />
          </div>
          <h1 className="text-3xl font-bold text-white font-heading mb-2">
            Super Admin Portal
          </h1>
          <p className="text-slate-400 font-body">
            Secure administrative access for Business Jadhak platform
          </p>
        </div>

        {/* Login Form Card */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-8 elevation-lg">
          <form onSubmit={handleLogin} className="space-y-6">
            {/* Email Field */}
            <Input
              type="email"
              label="Admin Email"
              placeholder="admin@businessjadhak.com"
              value={formData?.email}
              onChange={(e) => handleInputChange('email', e?.target?.value)}
              error={errors?.email}
              required
              className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500"
            />

            {/* Password Field */}
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                label="Password"
                placeholder="Enter your admin password"
                value={formData?.password}
                onChange={(e) => handleInputChange('password', e?.target?.value)}
                error={errors?.password}
                required
                className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-9 text-slate-400 hover:text-slate-300 transition-colors"
              >
                <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
              </button>
            </div>

            {/* Two-Factor Authentication */}
            {showTwoFactor && (
              <Input
                type="text"
                label="Two-Factor Authentication Code"
                placeholder="Enter 6-digit code"
                value={formData?.twoFactorCode}
                onChange={(e) => handleInputChange('twoFactorCode', e?.target?.value)}
                error={errors?.twoFactorCode}
                required
                maxLength={6}
                className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500"
              />
            )}

            {/* Forgot Password Link */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-slate-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showTwoFactor}
                  onChange={(e) => setShowTwoFactor(e?.target?.checked)}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-900/50 text-amber-500 focus:ring-amber-500"
                />
                Enable 2FA
              </label>
              <button
                type="button"
                onClick={handleForgotPassword}
                className="text-sm text-amber-500 hover:text-amber-400 transition-colors"
              >
                Forgot Password?
              </button>
            </div>

            {/* Login Button */}
            <Button
              type="submit"
              variant="default"
              size="lg"
              fullWidth
              loading={isLoading}
              iconName="Lock"
              iconPosition="left"
              className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
            >
              {isLoading ? 'Authenticating...' : 'Secure Login'}
            </Button>
          </form>

          {/* Security Notice */}
          <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
            <div className="flex items-start gap-3">
              <Icon name="AlertTriangle" size={18} color="#F59E0B" className="mt-0.5" />
              <div>
                <p className="text-sm text-amber-500 font-medium mb-1">
                  Security Notice
                </p>
                <p className="text-xs text-slate-400">
                  All login attempts are monitored and logged. Unauthorized access attempts will be reported.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-6 text-center">
          <div className="flex items-center justify-center gap-4 text-sm text-slate-500">
            <button className="hover:text-slate-400 transition-colors">
              Support Escalation
            </button>
            <span>•</span>
            <button className="hover:text-slate-400 transition-colors">
              System Status
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminLogin;